import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import api from '@/lib/api';
import { thumbUrl } from '@/lib/imageUrl';
import { ArrowLeft, MessageCircle, Eye, Clock, Pin, Lock, Trash2, Send, Pencil, Lock as LockIcon } from 'lucide-react';
import ForumContent from '@/components/ForumContent';
import ForumEditor from '@/components/ForumEditor';
import ShareButton from '@/components/ShareButton';
import { isPaidTier } from '@/lib/tiers';

// The "Introduce Yourself" welcome thread is the one onboarding surface free
// members can read AND reply to — every other thread still requires a paid
// membership.
const WELCOME_THREAD_ID = 'intro-yourself-2026';

function AuthorAvatar({ authorId, authorName, authorPhoto, size = 'sm' }) {
  const photoUrl = thumbUrl(authorPhoto);
  const dim = size === 'lg' ? 'w-10 h-10 text-sm' : 'w-7 h-7 text-xs';
  return (
    <Link to={`/player/${authorId}`} className="flex-shrink-0" data-testid={`author-avatar-${authorId}`}>
      {photoUrl ? (
        <img src={photoUrl} alt={authorName} loading="lazy" decoding="async" className={`${dim} rounded-full object-cover hover:ring-2 ring-[#56dfb4] transition-all`} />
      ) : (
        <div className={`${dim} rounded-full bg-[#56dfb4]/15 flex items-center justify-center font-bold hover:ring-2 ring-[#56dfb4] transition-all`}>
          {(authorName || 'U')[0].toUpperCase()}
        </div>
      )}
    </Link>
  );
}

export default function ForumThreadPage() {
  const { threadId } = useParams();
  const { user } = useAuth();
  const [thread, setThread] = useState(null);
  const [replies, setReplies] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  // Admin inline edit state for the OP question itself. Plain editor (not the
  // rich editor) so an admin can strip emojis/odd characters quickly without
  // worrying about HTML formatting controls.
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const [editBody, setEditBody] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);

  const isPaid = isPaidTier(user);
  const isAdmin = user?.role === 'admin';
  const isWelcomeThread = threadId === WELCOME_THREAD_ID;
  // Free members can view + reply to the welcome thread only. Every other
  // thread shows a soft paywall card with an Upgrade CTA.
  const canRead = isPaid || isWelcomeThread;
  const canReply = isPaid || isWelcomeThread;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(`/forum/threads/${threadId}`).then(({ data }) => {
      if (!cancelled) { setThread(data.thread); setReplies(data.replies || []); setLoading(false); }
    }).catch(() => { if (!cancelled) { setLoading(false); toast.error('Thread not found'); } });
    return () => { cancelled = true; };
  }, [threadId]);

  const handleReply = async () => {
    if (!replyText.trim()) return;
    setPosting(true);
    try {
      await api.post(`/forum/threads/${threadId}/reply`, { body: replyText });
      setReplyText('');
      // Refresh
      const { data } = await api.get(`/forum/threads/${threadId}`);
      setThread(data.thread);
      setReplies(data.replies || []);
      toast.success('Reply posted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to post reply');
    } finally { setPosting(false); }
  };

  const handleDeleteReply = async (replyId) => {
    if (!window.confirm('Delete this reply?')) return;
    try {
      await api.delete(`/forum/replies/${replyId}`);
      setReplies(prev => prev.filter(r => r.reply_id !== replyId));
      toast.success('Reply deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const handleDeleteThread = async () => {
    if (!window.confirm('Delete this entire thread?')) return;
    try {
      await api.delete(`/forum/threads/${threadId}`);
      toast.success('Thread deleted');
      window.history.back();
    } catch { toast.error('Failed to delete'); }
  };

  const startEditThread = () => {
    if (!thread) return;
    setEditTitle(thread.title || '');
    setEditBody(thread.body || '');
    setEditing(true);
  };

  const saveEditThread = async () => {
    if (!editTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }
    setSavingEdit(true);
    try {
      await api.put(`/forum/threads/${threadId}`, { title: editTitle.trim(), body: editBody });
      setThread(prev => ({ ...prev, title: editTitle.trim(), body: editBody }));
      setEditing(false);
      toast.success('Thread updated');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save');
    } finally {
      setSavingEdit(false);
    }
  };

  const handleToggle = async (field) => {
    try {
      await api.put(`/forum/threads/${threadId}`, { [field]: !thread[field] });
      setThread(p => ({ ...p, [field]: !p[field] }));
      toast.success(`Thread ${field === 'pinned' ? (thread.pinned ? 'unpinned' : 'pinned') : (thread.locked ? 'unlocked' : 'locked')}`);
    } catch { toast.error('Failed to update'); }
  };

  if (loading) return <div className="flex items-center justify-center min-h-[60vh]"><div className="animate-spin w-8 h-8 border-4 border-[#56dfb4] border-t-transparent rounded-full" /></div>;
  if (!thread) return <div className="text-center py-20 text-slate-400">Thread not found</div>;

  // Free members hitting a non-welcome thread see the upgrade wall here
  // (App.js routes this page through ProtectedRoute, not PaidRoute, so the
  // welcome thread can render for everyone).
  if (!canRead) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4" data-testid="upgrade-prompt-thread">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 rounded-2xl bg-[#b456df]/10 flex items-center justify-center mx-auto mb-4">
            <LockIcon className="w-8 h-8 text-[#b456df]" />
          </div>
          <h2 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Members-Only Discussion</h2>
          <p className="text-slate-500 mb-6">A paid membership unlocks the full Discussion Board. Free members can introduce themselves on our welcome thread.</p>
          <div className="flex items-center justify-center gap-3">
            <Link to="/subscription">
              <Button className="rounded-full bg-[#b456df] text-white hover:bg-[#9a3ec4] px-8" data-testid="upgrade-btn">Upgrade Now</Button>
            </Link>
            <Link to={`/forum/thread/${WELCOME_THREAD_ID}`}>
              <Button variant="outline" className="rounded-full px-6" data-testid="goto-welcome-thread-btn">👋 Welcome Thread</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="forum-thread-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <Link to={`/forum/${thread.category}`} className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-6">
        <ArrowLeft className="w-4 h-4" /> Back to {thread.category}
      </Link>

      {/* Thread */}
      <Card className="border-slate-100 mb-6">
        <CardContent className="p-6">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex flex-wrap items-center gap-2">
              {thread.pinned && <Badge className="rounded-full bg-amber-100 text-amber-700 text-xs gap-1"><Pin className="w-3 h-3" />Pinned</Badge>}
              {thread.locked && <Badge className="rounded-full bg-slate-100 text-slate-500 text-xs gap-1"><Lock className="w-3 h-3" />Locked</Badge>}
              {thread.tags?.map(tag => <Badge key={tag} variant="outline" className="rounded-full text-xs">{tag}</Badge>)}
            </div>
            <div className="flex items-center gap-1">
              <ShareButton
                shareApiPath={`/api/forum/threads/${thread.thread_id}/share`}
                title={thread.title}
                subtitle={thread.author_name ? `Discussion by ${thread.author_name}` : ''}
                testId="share-thread-btn"
              />
              {(isAdmin || thread.author_id === user?.id) && !editing && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={startEditThread}
                  className="text-xs gap-1"
                  data-testid="thread-edit-btn"
                >
                  <Pencil className="w-3 h-3" /> Edit
                </Button>
              )}
              {isAdmin && (
                <>
                  <Button variant="ghost" size="sm" onClick={() => handleToggle('pinned')} className="text-xs">{thread.pinned ? 'Unpin' : 'Pin'}</Button>
                  <Button variant="ghost" size="sm" onClick={() => handleToggle('locked')} className="text-xs">{thread.locked ? 'Unlock' : 'Lock'}</Button>
                  <Button variant="ghost" size="sm" onClick={handleDeleteThread} className="text-red-500 text-xs"><Trash2 className="w-3 h-3" /></Button>
                </>
              )}
            </div>
          </div>
          <h1 className="text-2xl font-bold mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>{thread.title}</h1>
          <div className="flex items-center gap-3 text-sm text-slate-500 mb-4">
            <AuthorAvatar authorId={thread.author_id} authorName={thread.author_name} authorPhoto={thread.author_photo} size="lg" />
            <div>
              <Link to={`/player/${thread.author_id}`} className="font-medium text-slate-700 hover:text-[#56dfb4] transition-colors">{thread.author_name}</Link>
              <div className="flex items-center gap-3 text-xs text-slate-400">
                <span>{thread.created_at ? new Date(thread.created_at).toLocaleDateString() : ''}</span>
                <span className="flex items-center gap-1"><Eye className="w-3 h-3" />{thread.views}</span>
                <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" />{thread.reply_count}</span>
              </div>
            </div>
          </div>
          {editing ? (
            // Admin inline-edit form. Plain inputs (not the rich-text editor)
            // so it's easy to scrub problematic Unicode / emojis from a
            // pasted body. Saves via PUT /forum/threads/{id} which only
            // admins/authors can hit.
            <div className="space-y-3 p-4 rounded-xl bg-slate-50 border border-slate-200" data-testid="admin-edit-thread-form">
              <input
                type="text"
                value={editTitle}
                onChange={(e) => setEditTitle(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#56dfb4] focus:outline-none text-base"
                placeholder="Thread title"
                data-testid="admin-edit-thread-title"
              />
              <textarea
                value={editBody}
                onChange={(e) => setEditBody(e.target.value)}
                rows={10}
                className="w-full px-3 py-2 rounded-lg border border-slate-300 focus:border-[#56dfb4] focus:outline-none text-sm font-mono"
                placeholder="Thread body (HTML allowed)"
                data-testid="admin-edit-thread-body"
              />
              <div className="flex gap-2">
                <Button onClick={saveEditThread} disabled={savingEdit} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-1" data-testid="admin-edit-thread-save">
                  {savingEdit ? 'Saving...' : 'Save'}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} className="rounded-full" data-testid="admin-edit-thread-cancel">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="prose max-w-none text-slate-700 leading-relaxed text-sm">
              <ForumContent text={thread.body} />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Replies */}
      <h3 className="font-semibold mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {replies.length} {replies.length === 1 ? 'Reply' : 'Replies'}
      </h3>
      <div className="space-y-3 mb-8">
        {replies.map(r => (
          <Card key={r.reply_id} className="border-slate-100" data-testid={`reply-${r.reply_id}`}>
            <CardContent className="p-5">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 text-sm">
                  <AuthorAvatar authorId={r.author_id} authorName={r.author_name} authorPhoto={r.author_photo} />
                  <div>
                    <Link to={`/player/${r.author_id}`} className="font-medium hover:text-[#56dfb4] transition-colors">{r.author_name}</Link>
                    <div className="flex items-center gap-2 text-xs text-slate-400">
                      <span>{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                      {r.edited && <span>(edited)</span>}
                    </div>
                  </div>
                </div>
                {(isAdmin || r.author_id === user?.id) && (
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteReply(r.reply_id)} className="text-red-400 hover:text-red-500">
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
              <div className="text-sm text-slate-700 leading-relaxed"><ForumContent text={r.body} /></div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Reply Form */}
      {canReply && !thread.locked ? (
        <Card className="border-slate-100">
          <CardContent className="p-5">
            <h3 className="font-semibold text-sm mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>Post a Reply</h3>
            <ForumEditor value={replyText} onChange={setReplyText} rows={4} placeholder="Share your thoughts..." testId="reply-editor" />
            <Button onClick={handleReply} disabled={posting || !replyText.trim()} className="rounded-full bg-[#56dfb4] text-slate-900 hover:bg-[#3ec49b] gap-2 mt-3" data-testid="post-reply-btn">
              <Send className="w-4 h-4" />{posting ? 'Posting...' : 'Post Reply'}
            </Button>
          </CardContent>
        </Card>
      ) : thread.locked ? (
        <p className="text-center text-slate-400 text-sm py-4">This thread is locked and no longer accepting replies.</p>
      ) : !canReply && user ? (
        <div className="text-center py-4">
          <p className="text-slate-500 text-sm">A <Link to="/subscription" className="text-[#b456df] hover:underline">paid membership</Link> is required to reply.</p>
        </div>
      ) : null}
    </div>
  );
}
